import {
  h,
  createContext,
  FunctionalComponent,
  ComponentChildren,
} from "preact";
import { useState, useContext, useEffect, useRef } from "preact/hooks";
import { getProducts } from "../api";
import type { Filter, SelectedFilter, Product, Pagination } from "../types";

// Define the props expected by the CollectionProvider component
interface CollectionProviderProps {
  children: ComponentChildren;
  collectionId: number;
  productsPerPage: number;
  searchTerm: string;
  currentPage: number;
  pages: number;
}

// Define the structure of the CollectionContext
interface CollectionContextType {
  products: Product[] | "pending";
  productCount: number;
  pagination: Pagination;
  selectedSort: string;
  filters: Filter[];
  selectedFilters: SelectedFilter[];
  setSelectedFilters: (filters: SelectedFilter[]) => void;
  setPagination: (pagination: Pagination) => void;
  setSelectedSort: (sort: string) => void;
  fetchProducts: (
    selectedFilters: SelectedFilter[],
    pagination: Pagination,
    selectedSort?: string,
    append?: boolean,
    prepend?: boolean,
  ) => void;
  clearFilters: () => void; // Add clearFilters function
}

// Create the CollectionContext with an initial undefined value
const CollectionContext = createContext<CollectionContextType | undefined>(
  undefined,
);

// Define the CollectionProvider component
export const CollectionProvider: FunctionalComponent<
  CollectionProviderProps
> = ({ children, productsPerPage, currentPage }) => {
  const searchParams = new URLSearchParams(window.location.search);
  const initialFilters: SelectedFilter[] = [];
  searchParams.forEach((value, key) => {
    if (key.startsWith("filter")) {
      initialFilters.push({
        param_name: key,
        values: value.split(","),
        type: "checkbox",
      });
    }
  });
  // State to store the products, initially set to "pending"
  const [products, setProducts] = useState<Product[] | "pending">("pending");
  // State to store the total count of products
  const [productCount, setProductCount] = useState(0);
  // State to manage pagination details
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: searchParams.has("page")
      ? Number(searchParams.get("page"))
      : currentPage,
    perPage: productsPerPage,
    pages: 1,
  });

  // State to store available filters
  const [filters, setFilters] = useState<Filter[]>([]);
  // State to store selected filters
  const [selectedFilters, setSelectedFilters] =
    useState<SelectedFilter[]>(initialFilters);
  // State to store the selected sort option
  const [selectedSort, setSelectedSort] = useState<string>(
    searchParams.has("sort") ? searchParams.get("sort") || "manual" : "manual",
  );

  const isFirstRender = useRef(true);

  useEffect(() => {
    let currentPagination = pagination;
    if (!isFirstRender.current) {
      // Skip resetting pagination on the initial render
      setPagination((prevPagination) => ({
        ...prevPagination,
        currentPage: 1,
      }));
      currentPagination = { ...pagination, currentPage: 1 };
    }

    (async () => {
      await fetchProducts(selectedFilters, currentPagination, selectedSort);
      if (isFirstRender.current && currentPagination.currentPage !== 1) {
        // Get product hanlde of the previous PDP page visited from session storage
        const productHandle = sessionStorage.getItem("productHandle");
        if (productHandle) {
          // Scroll to the product card wich ID matches the product handle
          const productCard = document.getElementById(productHandle);
          if (productCard) {
            productCard.scrollIntoView();
            sessionStorage.removeItem("productHandle");
          }
        }
      }
      if (isFirstRender.current) {
        isFirstRender.current = false;
      }
    })();
  }, [selectedFilters, selectedSort]);

  // Function to fetch products based on filters, pagination, sort, and search term
  const fetchProducts = async (
    selectedFilters: SelectedFilter[],
    pagination: Pagination,
    selectedSort?: string | undefined,
    append?: boolean | undefined,
    prepend?: boolean | undefined,
  ) => {
    const data = await getProducts(
      "SECTION_RENDERING",
      selectedFilters,
      pagination,
      selectedSort,
    );

    const searchParams = new URLSearchParams(window.location.search);
    if (!prepend) {
      searchParams.set(
        "page",
        pagination.currentPage ? pagination.currentPage.toString() : "1",
      );
    }
    searchParams.set("sort", selectedSort || "manual");
    for (const key of searchParams.keys()) {
      if (
        key.startsWith("filter") &&
        !selectedFilters.some((filter) => filter.param_name === key)
      ) {
        searchParams.delete(key);
      }
    }
    if (selectedFilters.length > 0) {
      for (const filter of selectedFilters) {
        searchParams.set(filter.param_name, filter.values.join(","));
      }
    }

    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${searchParams.toString()}`,
    );

    // Update the products state, appending new products if 'append' is true
    setProducts((prevProducts) =>
      Array.isArray(prevProducts) && Array.isArray(data.products)
        ? append
          ? [...prevProducts, ...data.products] // Append if 'append' is true
          : prepend
            ? [...data.products, ...prevProducts] // Prepend if 'prepend' is true
            : data.products // If neither, replace with new data
        : data.products,
    );

    // Update the product count state
    setProductCount(data.productCount);
    // Update the pagination state to reflect the new pagination data
    if (!prepend) setPagination(data.pagination);
    // Update the filters state with the filters from the fetched data
    setFilters(data.filters);
  };

  // Function to clear all selected filters
  const clearFilters = () => {
    setSelectedFilters([]);
  };

  return (
    <CollectionContext.Provider
      value={{
        products,
        productCount,
        pagination,
        filters,
        selectedFilters,
        selectedSort,
        setPagination,
        setSelectedSort,
        setSelectedFilters,
        fetchProducts,
        clearFilters, // Add clearFilters to the provider value
      }}
    >
      {children}
    </CollectionContext.Provider>
  );
};

// Custom hook to use the CollectionContext
export const useCollection = () => {
  const context = useContext(CollectionContext);
  if (context === undefined) {
    throw new Error("useCollection must be used within a CollectionProvider");
  }
  return context;
};
